import {useRouter} from 'next/router';
import React, {ChangeEvent, useState} from 'react';

import {I18nApi, useI18n} from '@udemy/i18n';
import {IconName} from '@udemy/icons';
import {FormGroup, Select} from '@udemy/react-form-components';
import {
    MenuIcon,
    MenuLabel,
    MenuLink,
    MenuTitle,
    Menu,
    SubMenu,
    SubMenuItem,
} from '@udemy/react-menu-layout-components';

import {TERM_PAGE_BASE_PATH} from '../../constants';
import styles from './legal-pages-nav.module.less';

export const LEGAL_PAGES_INTRO = (gettext: I18nApi['gettext']) => ({
    label: gettext('Terms & Policies'),
    slug: `/${TERM_PAGE_BASE_PATH}/`,
});

export const DEFAULT_LEGAL_PAGES_NAV = (gettext: I18nApi['gettext']) => [
    {
        label: gettext('Marketplace & Website'),
        icon: 'people',
        items: [
            {slug: `/${TERM_PAGE_BASE_PATH}/terms-of-use/`, label: gettext('Terms of Use')},
            {slug: `/${TERM_PAGE_BASE_PATH}/privacy/`, label: gettext('Privacy Policy')},
        ],
    },
    {
        label: gettext('Instructors'),
        icon: 'teach',
        items: [
            {slug: `/${TERM_PAGE_BASE_PATH}/instructor/`, label: gettext('Instructor Terms')},
            {slug: `/${TERM_PAGE_BASE_PATH}/promotions/`, label: gettext('Promotions Policy')},
            {
                slug: `/${TERM_PAGE_BASE_PATH}/genai-instructor-policy/`,
                label: gettext('Instructor Generative AI Policy'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/subscription-bonus/`,
                label: gettext('Subscription Bonus Policy'),
            },
        ],
    },
    {
        label: gettext('Udemy Business'),
        icon: 'marketing',
        items: [
            {slug: `/${TERM_PAGE_BASE_PATH}/ub/`, label: gettext('Master Services Agreement')},
            {
                slug: `/${TERM_PAGE_BASE_PATH}/msa-india/`,
                label: gettext('India Master Service Agreement'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/corpu-terms/`,
                label: gettext('Leadership Academy Terms & Conditions'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/upro-terms/`,
                label: gettext('Pro Terms & Conditions'),
            },
            {slug: `/${TERM_PAGE_BASE_PATH}/launch-services/`, label: gettext('Launch Services')},
            {
                slug: `/${TERM_PAGE_BASE_PATH}/ub-privacy/`,
                label: gettext('Privacy Statement'),
            },
            {slug: `/${TERM_PAGE_BASE_PATH}/dpa/`, label: gettext('Data Processing Addendum')},
            {slug: `https://trust.udemy.com`, label: gettext('Trust Hub')},
            {
                slug: `/${TERM_PAGE_BASE_PATH}/brazil/`,
                label: gettext('Brazil Addendum'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/premium-support/`,
                label: gettext('Premium Support Terms & Conditions'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/ai-connector/`,
                label: gettext('AI Connector Terms'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/team-plan-save-offer/`,
                label: gettext('Team Plan - Save Offer Terms'),
            },
        ],
    },
    {
        label: gettext('Suppliers'),
        icon: 'holdout',
        items: [
            {slug: `/${TERM_PAGE_BASE_PATH}/mnda/`, label: gettext('Mutual NDA')},
            {
                slug: `/${TERM_PAGE_BASE_PATH}/msa-vendor/`,
                label: gettext('Master Services Agreement (Vendor)'),
            },
            {slug: `/${TERM_PAGE_BASE_PATH}/saa/`, label: gettext('Systems Access Addendum')},
            {
                slug: `/${TERM_PAGE_BASE_PATH}/po/`,
                label: gettext('Purchase Order Terms & Conditions'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/data-processing-agreement/`,
                label: gettext('Data Processing Agreement'),
            },
            {
                slug: `https://about.udemy.com/wp-content/uploads/2024/10/Supplier-Code-of-Conduct.pdf `,
                label: gettext('Supplier Code of Conduct'),
            },
            {
                slug: `https://about.udemy.com/esg-at-udemy/`,
                label: gettext('ESG/Supplier Diversity'),
            },
        ],
    },
    {
        label: gettext('Partners'),
        icon: 'groups',
        items: [{slug: `https://partnerportal.udemy.com`, label: gettext('Partner Portal')}],
    },
    {
        label: gettext('Additional Terms & Resources'),
        icon: 'assignment-outline',
        items: [
            {
                slug: `/${TERM_PAGE_BASE_PATH}/responsible-ai-principles/`,
                label: gettext('Responsible AI Principles'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/copyright/`,
                label: gettext('Intellectual Property Policy'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/affiliate/`,
                label: gettext('Affiliate Terms'),
            },
            {slug: `/${TERM_PAGE_BASE_PATH}/api/`, label: gettext('Udemy API Agreement')},
            {slug: `/${TERM_PAGE_BASE_PATH}/beta/`, label: gettext('Beta Terms')},
            {
                slug: `https://investors.udemy.com/static-files/fd697ea5-17b6-4536-bfe2-5539e84305f3`,
                label: gettext('Code of Conduct'),
            },
            {
                slug: `https://about.udemy.com/accessibility-statement/`,
                label: gettext('Accessibility Statement'),
            },
            {slug: `/${TERM_PAGE_BASE_PATH}/events/`, label: gettext('Event Terms & Conditions')},
            {
                slug: `/${TERM_PAGE_BASE_PATH}/digital-services-act/`,
                label: gettext('Digital Services Act Information: European Union Users Only'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/pa-terms/`,
                label: gettext('Payment Agent Terms'),
            },
            {
                slug: `/${TERM_PAGE_BASE_PATH}/learn-with-google/`,
                label: gettext('Learn With Google'),
            },
        ],
    },
];

export function LegalPagesNavAsDropdown() {
    const {gettext} = useI18n();
    const router = useRouter();
    const activeHref = router.asPath.split('?')[0];
    const LEGAL_PAGES_NAV = DEFAULT_LEGAL_PAGES_NAV(gettext);
    const LEGAL_PAGES_NAV_INTRO = LEGAL_PAGES_INTRO(gettext);

    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
        e.preventDefault();
        const selectedUrl = e.currentTarget.value;
        if (!selectedUrl) return;
        const queryParams = new URLSearchParams(window.location.search);
        const queryString = queryParams.toString();
        const url = queryString ? `${selectedUrl}?${queryString}` : selectedUrl;
        if (selectedUrl.startsWith('http://') || selectedUrl.startsWith('https://')) {
            window.location.href = selectedUrl;
        } else {
            router.push(url, undefined, {shallow: true});
        }
    };

    return (
        <div className={styles['legal-pages-nav__dropdown']}>
            <FormGroup labelProps={{className: 'ud-sr-only'}} label={''}>
                <Select onChange={handleChange} size={'medium'} value={activeHref}>
                    <optgroup label={LEGAL_PAGES_NAV_INTRO.label} key={LEGAL_PAGES_NAV_INTRO.label}>
                        <option value={LEGAL_PAGES_NAV_INTRO.slug} key={LEGAL_PAGES_NAV_INTRO.slug}>
                            {LEGAL_PAGES_NAV_INTRO.label}
                        </option>
                    </optgroup>
                    {LEGAL_PAGES_NAV.map(({label, items}) => (
                        <optgroup label={label} key={label}>
                            {items.map(({slug, label}) => (
                                <option value={slug} key={slug}>
                                    {label}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </Select>
            </FormGroup>
        </div>
    );
}

export function LegalPagesNavAsSideBar() {
    const {gettext} = useI18n();
    const router = useRouter();
    const activeHref = router.asPath.split('?')[0];
    const LEGAL_PAGES_NAV = DEFAULT_LEGAL_PAGES_NAV(gettext);
    const LEGAL_PAGES_NAV_INTRO = LEGAL_PAGES_INTRO(gettext);

    const [isOpen, setIsOpen] = useState(true);

    // For demo purposes only; replace with your own link component
    const linkDemoProps = (href: string) => ({
        disableRouter: true,
        onClick: (event: React.MouseEvent) => {
            event.preventDefault();
            const queryParams = new URLSearchParams(window.location.search);
            const queryString = queryParams.toString();
            const url = queryString ? `${href}?${queryString}` : href;
            if (href.startsWith('http://') || href.startsWith('https://')) {
                window.location.href = href;
            } else {
                router.push(url, undefined, {shallow: true});
            }
        },
    });

    return (
        <Menu
            isOpen={isOpen}
            onToggleOpen={() => setIsOpen((open) => !open)}
            title={
                <MenuTitle to="" {...linkDemoProps(LEGAL_PAGES_NAV_INTRO.slug)}>
                    {LEGAL_PAGES_NAV_INTRO.label}
                </MenuTitle>
            }
        >
            {LEGAL_PAGES_NAV.map((submenu, index) => {
                // Check if the activeHref exists in the submenu's items
                const isSubMenuExpanded = submenu.items.some((item) => activeHref === item.slug);

                return (
                    <SubMenu
                        key={index}
                        label={
                            <MenuLabel icon={<MenuIcon glyph={submenu.icon as IconName} />}>
                                {submenu.label}
                            </MenuLabel>
                        }
                        isDefaultExpanded={isSubMenuExpanded}
                    >
                        {submenu.items.map((item, subIndex) => (
                            <SubMenuItem key={subIndex} isActive={activeHref === item.slug}>
                                <MenuLink to={item.slug} {...linkDemoProps(item.slug)}>
                                    <MenuLabel>{item.label}</MenuLabel>
                                </MenuLink>
                            </SubMenuItem>
                        ))}
                    </SubMenu>
                );
            })}
        </Menu>
    );
}
